import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { Link2, Search, Unlink, Users } from 'lucide-react';
import toast from 'react-hot-toast';
import { clientsAPI, clientScheduleGroupsAPI } from '@shared/apiClient';

const getDisplayName = (c) => {
  if (c.isCommercial && c.companyName) return c.companyName;
  const ln = c.contactLastName?.trim();
  const fn = c.contactFirstName?.trim();
  if (ln && fn) return `${ln}, ${fn}`;
  return ln || fn || c.companyName || 'Unknown';
};

export default function ClientScheduleGroupSection({ client }) {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [groupName, setGroupName] = useState('');

  const { data: clientData, refetch } = useQuery(
    ['client', client.id, 'scheduleGroup'],
    () => clientsAPI.getClient(client.id),
    { enabled: !!client?.id }
  );

  const fullClient = clientData?.data?.client || client;
  const scheduleGroup = fullClient.scheduleGroup;
  const groupMembers = scheduleGroup?.clients || [];

  const { data: searchData } = useQuery(
    ['clients', 'scheduleGroupSearch', search],
    () => clientsAPI.getClients({ search, limit: 20, isActive: true }),
    { enabled: search.trim().length >= 2 }
  );

  const searchResults = (searchData?.data?.clients || []).filter((c) => c.id !== client.id);

  const invalidate = () => {
    queryClient.invalidateQueries(['client', client.id]);
    queryClient.invalidateQueries('clients');
    queryClient.invalidateQueries('clientScheduleGroups');
    queryClient.invalidateQueries('schedulingBacklog');
    refetch();
  };

  const createGroupMutation = useMutation(
    (data) => clientScheduleGroupsAPI.create(data),
    {
      onSuccess: () => {
        toast.success('Schedule group created');
        setGroupName('');
        setSearch('');
        invalidate();
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to create group');
      },
    }
  );

  const addClientMutation = useMutation(
    ({ groupId, clientId }) => clientScheduleGroupsAPI.addClient(groupId, clientId),
    {
      onSuccess: () => {
        toast.success('Client linked to group');
        setSearch('');
        invalidate();
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to link client');
      },
    }
  );

  const removeClientMutation = useMutation(
    ({ groupId, clientId }) => clientScheduleGroupsAPI.removeClient(groupId, clientId),
    {
      onSuccess: () => {
        toast.success('Client removed from group');
        invalidate();
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to remove client');
      },
    }
  );

  const handleCreateGroup = () => {
    const name = groupName.trim() || `Linked: ${getDisplayName(client)}`;
    createGroupMutation.mutate({ name, clientIds: [client.id] });
  };

  const handleLinkExisting = (otherClient) => {
    if (scheduleGroup) {
      addClientMutation.mutate({ groupId: scheduleGroup.id, clientId: otherClient.id });
    } else {
      const name = groupName.trim() || `Linked: ${getDisplayName(client)} & ${getDisplayName(otherClient)}`;
      createGroupMutation.mutate({ name, clientIds: [client.id, otherClient.id] });
    }
  };

  if (!client?.id) return null;

  return (
    <div className="sm:col-span-2 border-t border-gray-200 pt-4 mt-2">
      <label className="block text-sm font-medium text-gray-700 mb-2">
        <Link2 className="inline h-4 w-4 mr-1" />
        Schedule Together (linked clients)
      </label>
      <p className="text-xs text-gray-500 mb-3">
        Clients in the same group are always assigned to the same technician on the same day during auto-schedule.
      </p>

      {scheduleGroup ? (
        <div className="border border-purple-200 rounded-md p-3 bg-purple-50 mb-3">
          <div className="flex items-center gap-2 mb-2">
            <Users className="h-4 w-4 text-purple-700" />
            <span className="font-medium text-sm text-purple-900">{scheduleGroup.name}</span>
          </div>
          <ul className="space-y-1 mb-3">
            {groupMembers.map((member) => (
              <li key={member.id} className="flex items-center justify-between text-sm text-purple-900">
                <span>{getDisplayName(member)}</span>
                <button
                  type="button"
                  onClick={() => removeClientMutation.mutate({ groupId: scheduleGroup.id, clientId: member.id })}
                  className="text-purple-600 hover:text-purple-800 inline-flex items-center gap-1 text-xs"
                  disabled={removeClientMutation.isLoading}
                >
                  <Unlink className="h-3 w-3" />
                  {member.id === client.id ? 'Leave group' : 'Remove'}
                </button>
              </li>
            ))}
          </ul>
        </div>
      ) : (
        <div className="mb-3">
          <input
            type="text"
            value={groupName}
            onChange={(e) => setGroupName(e.target.value)}
            placeholder="Group name (optional)"
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm mb-2"
          />
          <button
            type="button"
            onClick={handleCreateGroup}
            disabled={createGroupMutation.isLoading}
            className="text-sm text-blue-600 hover:text-blue-800"
          >
            Create empty linked group for this client
          </button>
        </div>
      )}

      <div className="relative">
        <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search clients to link..."
          className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-md text-sm"
        />
      </div>
      {search.trim().length >= 2 && searchResults.length > 0 && (
        <ul className="mt-2 border border-gray-200 rounded-md divide-y divide-gray-100 max-h-40 overflow-auto">
          {searchResults.map((c) => (
            <li key={c.id}>
              <button
                type="button"
                onClick={() => handleLinkExisting(c)}
                className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 flex items-center justify-between"
                disabled={createGroupMutation.isLoading || addClientMutation.isLoading}
              >
                <span>{getDisplayName(c)}</span>
                {c.scheduleGroup && (
                  <span className="text-2xs text-purple-600">In: {c.scheduleGroup.name}</span>
                )}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
